import { Form } from "@inertiajs/react"
import { Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Alert, AlertDescription } from "@/components/ui/alert"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { cancelRedemptionPath } from "@/routes"

/**
 * OKS-61 — Cancel confirmation dialog (UX Spec §5).
 *
 * Wraps shadcn `AlertDialog`. Destructive twin of the Fulfill dialog.
 * Two important UX divergences from fulfill:
 *
 *   1. **Confirm CTA copy spells out the refund amount** —
 *      "Cancel + refund {N} coins" (UX-OQ-5). Forces the brand admin
 *      to acknowledge the coin movement before the spend reverses.
 *   2. **Cancel button copy is "Keep redemption"** (NOT bare "Cancel")
 *      to disambiguate from the destructive action above (which uses
 *      "Cancel" semantically in its own label).
 *   3. **Body copy branches on `rewardIsUnlimited`** — unlimited
 *      rewards drop the "and {reward}'s stock will be restored by 1"
 *      clause (UX-OQ-6); stock-restore is a no-op for unlimited and
 *      mentioning it would be misleading.
 *
 * # Server-side outcomes (UX Spec §5)
 *
 * Same Inertia `<Form method="patch">` posture as
 * `FulfillRedemptionDialog` — success closes, failure stays open
 * with in-dialog destructive `<Alert>`. The success flash carries the
 * pluralized refund-coins toast ("Cancelled — N coins refunded to
 * {creator}.") via the server-side i18n.
 *
 * # Crew hard rule compliance
 *
 *   - Inertia `<Form>` (NOT shadcn `Form` / RHF / zod).
 *   - js-routes URL via `cancelRedemptionPath(id)`.
 *   - Server reads `Current.brand` + `params[:id]` — empty body.
 */
interface CancelRedemptionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  redemptionId: number | null
  creatorName: string
  rewardTitle: string
  coinCost: number
  rewardIsUnlimited: boolean
}

export function CancelRedemptionDialog({
  open,
  onOpenChange,
  redemptionId,
  creatorName,
  rewardTitle,
  coinCost,
  rewardIsUnlimited,
}: CancelRedemptionDialogProps) {
  const { t } = useTranslation()

  if (redemptionId === null) {
    return null
  }

  // UX-OQ-6: unlimited rewards drop the stock-restore clause so the
  // copy reads cleanly in both cases. Two i18n keys (binary branch).
  const bodyKey = rewardIsUnlimited
    ? "rewards.redemptions.cancel_dialog.body_unlimited"
    : "rewards.redemptions.cancel_dialog.body_with_stock"

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="cancel-redemption-dialog">
        <Form
          action={cancelRedemptionPath(redemptionId)}
          method="patch"
          options={{ preserveScroll: true }}
          onSuccess={() => {
            onOpenChange(false)
          }}
        >
          {({ processing, errors }) => (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle data-testid="cancel-redemption-dialog-title">
                  {t("rewards.redemptions.cancel_dialog.title")}
                </AlertDialogTitle>
                <AlertDialogDescription data-testid="cancel-redemption-dialog-description">
                  {t(bodyKey, {
                    creator: creatorName,
                    reward: rewardTitle,
                    count: coinCost,
                  })}
                </AlertDialogDescription>
                <p className="text-muted-foreground text-sm">
                  {t("rewards.redemptions.cancel_dialog.body_hint")}
                </p>
              </AlertDialogHeader>

              {errors.base ? (
                <Alert
                  variant="destructive"
                  role="alert"
                  data-testid="cancel-redemption-dialog-error"
                  className="mt-2"
                >
                  <AlertDescription>{errors.base}</AlertDescription>
                </Alert>
              ) : null}

              <AlertDialogFooter>
                <AlertDialogCancel
                  type="button"
                  disabled={processing}
                  data-testid="cancel-redemption-dialog-cancel"
                >
                  {t("rewards.redemptions.cancel_dialog.cancel")}
                </AlertDialogCancel>
                {/* OKS-1023 — plain submit `Button`, NOT `AlertDialogAction`:
                    the Action is Base UI `Dialog.Close`, so the click would close
                    the dialog and unmount this `<Form>` with its `processing` and
                    `errors.base` before the server answers. */}
                <Button
                  type="submit"
                  disabled={processing}
                  aria-busy={processing}
                  variant="destructive"
                  data-testid="cancel-redemption-dialog-confirm"
                >
                  {processing ? (
                    <Loader2
                      aria-hidden="true"
                      className="mr-2 size-4 animate-spin"
                    />
                  ) : null}
                  {t("rewards.redemptions.cancel_dialog.confirm", {
                    count: coinCost,
                  })}
                </Button>
              </AlertDialogFooter>
            </>
          )}
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  )
}
